import { useEffect, useState } from "react";
import styles from "./fba-update-shipment-drawer.module.css";
import { BoxContentsProvider, FulfillmentType, LabelPrepPreference, TransportType, UpdateInboundBatchBody } from "@boxem/core-api-schema";
import { updateBatchV2 } from "@/api/fulfillment";
import { Address, useUserContext } from "@/contexts/UserContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { FbaBatch, useShipmentContext } from "@/contexts/ShipmentContext";
import Button from "@/components/forms/Button/Button";
import TextInput from "@/components/forms/TextInput/TextInput";
import Select from "@/components/forms/Select/Select";
import DatePicker from "@/components/forms/DatePicker";
import dayjs from "dayjs";
import { getBatchStep } from "../utils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Flex from "@/components/Flex/Flex";
import Divider from "@/components/Divider/Divider";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";

type FbaUpdateShipmentDrawerProps = {
  open: boolean;
  onClose: () => void;
  shipmentId?: number;
};

const getAddressPreviewString = (address: Address): string => {
  let addressValue = "";

  if (!address?.address1) return addressValue;

  if (!!address?.address1) addressValue = addressValue + `${address?.address1}, `;
  if (!!address?.address2) addressValue = addressValue + `${address?.address2}, `;
  if (!!address?.city) addressValue = addressValue + `${address?.city} `;
  if (!!address?.state) addressValue = addressValue + `${address?.state} `;
  if (!!address?.zipcode) addressValue = addressValue + `${address?.zipcode}`;

  return addressValue;
};

const defaultForm: UpdateInboundBatchBody = {
  shipmentName: '',
  // fulfillmentType: Type.Optional(Type.Enum(FulfillmentType)),
  fromAddressId: 0,
  shippingMethod: TransportType.SPD,
  // packingType: Type.Optional(Type.Enum(PackingType)),
  // labelingPreference: Type.Optional(Type.Enum(LabelPrepPreference)), // @TODO rename this field or enum?
  boxContents: BoxContentsProvider.Boxem,
  // printBoxLabelsStep3: Type.Optional(Type.Boolean()),
  labelingPreference: LabelPrepPreference.SELLER_LABEL,
  readyToShip: "",
};

const FbaUpdateShipmentDrawer = ({
  onClose,
  open,
}: FbaUpdateShipmentDrawerProps) => {
  const { addressBook } = useUserContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { currentBatch, setCurrentBatch } = useShipmentContext();

  const [boxContentsDisabled, setBoxContentsDisabled] = useState<boolean>(false);
  const [readyToShipDateErrorMessage, setReadyToShipDateErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [form, setForm] = useState<UpdateInboundBatchBody>(defaultForm);
  const stepNumber = getBatchStep(currentBatch);

  const submit = () => {
    setLoading(true);
    updateBatchV2(currentBatch.id, form).then((result) => {
      const batch = {...currentBatch, ...result} as FbaBatch;
      setCurrentBatch(batch);
      setForm(defaultForm);
      onClose();
    }).catch((errors) => {
      if (handleErrorResponseGenerically(errors)) {}
      onClose();
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (currentBatch && open) {
      const details: UpdateInboundBatchBody = {
        boxContents: currentBatch.boxContentsProvider,
        fromAddressId: currentBatch.fromId,
        fulfillmentType: FulfillmentType.FBA, // FBABatches are implicitly FBA. @TODO remove in v2
        labelingPreference: currentBatch.labelPrepPreference, // @TODO remove in v2
        shipmentName: currentBatch.name,
        shippingMethod: currentBatch.shipmentType,
        readyToShip: currentBatch.readyToShip,
      };
      setForm(details);
    }
  }, [currentBatch, open]);

  useEffect(() => {
    setReadyToShipDateErrorMessage(null);

    let invalid = false;
   
    if (form.readyToShip) {
      const d = new Date(form.readyToShip);
      const today = dayjs().startOf("day");
      if (dayjs(d).isBefore(today)) {
        setReadyToShipDateErrorMessage("Ready to ship date must be today, or in the future.");
        invalid = true;
      }
    } else {
      setReadyToShipDateErrorMessage("Ready to ship date is required.");
      invalid = true;
    }

    setSubmitDisabled(invalid);
  }, [form]);

  return (
    <Modal
      open={open}
      onOpenChange={() => {}}
      onClose={() => {
        setForm({});
        onClose();
      }}
      closeOnOutsideClick
      className={styles.modal}
    >
      <ModalHeader>
        <h3 className="strong">Update Shipment</h3>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        <form
          className={styles.createShipmentForm}
          name="create-shipment"
          onSubmit={() => false}
        >
          <div className={styles.groupLabel}>
            <h3>Details</h3>
          </div>
          <div className={styles.formItem}>
            <label htmlFor="shipmentName">
              Shipment name:
            </label>
            <TextInput
              inputId="shipmentName"
              disabled={loading}
              value={form.shipmentName || ""}
              onChange={v => setForm({...form, shipmentName: v})}
            />
          </div>

          {stepNumber === 0 && (
            // past step 1, you can't edit anything except the shipment name,
            // (and technically the 'ready to ship' date)
            <>
              <Divider />

              <div className={styles.groupLabel}>
                <h3>Shipping & Origin</h3>
              </div>
              <div className={styles.formItem}>
                <label htmlFor="fromAddressId">
                  Ship from:
                </label>
                <Select
                  fullWidth
                  id={"fromAddressId"}
                  disabled={loading}
                  options={addressBook}
                  placeholder={"Select an address"}
                  value={addressBook.find(a => a.id === form.fromAddressId)}
                  onChange={(option) => {
                    setForm({...form, fromAddressId: option.id });
                  }}
                  optionRender={(option) => option.name}
                />
              </div>
              {form.fromAddressId !== 0 && (
                <p className={styles.addressPreviewText}>{getAddressPreviewString(addressBook.find(a => a.id === form.fromAddressId))}</p>
              )}

              <div className={styles.formItem}>
                <label htmlFor="shippingMethod">
                  Shipping method:
                </label>
                <Select
                  id={"shippingMethod"}
                  fullWidth
                  disabled={loading}
                  value={form.shippingMethod}
                  onChange={value => {
                    const diffs = { shippingMethod: value } as any;
                    if (value === TransportType.LTL) {
                      diffs.boxContents = BoxContentsProvider.Barcode2D; // 2D barcodes are forced for LTL shipments
                      setBoxContentsDisabled(true);
                    } else {
                      setBoxContentsDisabled(false);
                    }
                    setForm({...form, ...diffs});
                  }}
                  options={[TransportType.SPD, TransportType.LTL]}
                  optionRender={option => {
                    if (option === TransportType.SPD) {
                      return "SPD (Small Parcel Delivery)";
                    }  else {
                      return "LTL (Less than Truck Load)";
                    }
                  }}
                />
              </div>
            </>
          )}

          {stepNumber <= 1 && (
            <Flex direction="column">
              <div className={styles.formItem}>
                <label htmlFor="readyToShip">
                  Ready to ship:
                </label>
                <DatePicker
                  invalid={!!readyToShipDateErrorMessage}
                  minDate={new Date()}
                  value={form.readyToShip ? new Date(form.readyToShip) : null}
                  onChange={v => {
                    setForm({...form, readyToShip: v.toISOString()})
                  }}
                />
              </div>
              {readyToShipDateErrorMessage && (
                <p className={styles.invalidText}>{readyToShipDateErrorMessage}</p>
              )}
            </Flex>
          )}
          
          {stepNumber === 0 && (
            <>
              <Divider />

              <div className={styles.groupLabel}>
                <h3>Packaging & Labeling</h3>
              </div>
              <div className={styles.formItem}>
                <label htmlFor="boxContents">
                  Box contents:
                </label>
                <Select            
                  fullWidth
                  id="boxContents"
                  disabled={boxContentsDisabled || loading}
                  options={[BoxContentsProvider.Boxem,BoxContentsProvider.Seller,BoxContentsProvider.Barcode2D]}
                  optionRender={option => {
                    switch (option) {
                      case BoxContentsProvider.Boxem: return "Boxem Provided";
                      case BoxContentsProvider.Seller: return "No Box contents";
                      case BoxContentsProvider.Barcode2D: return "2D Barcodes";
                    }
                  }}
                  value={form.boxContents}
                  onChange={option => setForm({...form, boxContents: option})}
                />
              </div>
            </>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between">
          <Button
            type="secondary"
            size="large"
            style={{ marginRight: "1rem" }}
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button type="primary" size="large" onClick={submit} disabled={loading || submitDisabled}>
            {loading && <LoadingSpinner size="2rem" />}
            Update shipment
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default FbaUpdateShipmentDrawer;
